import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useActiveWorkspaceStore } from '@/stores/active-workspace';

export type WorkspaceRole = 'owner' | 'admin' | 'agent';

export type Workspace = {
  workspace_id: string;
  name: string;
  timezone: string;
  role: WorkspaceRole;
  created_at: string;
};

interface MeResponse {
  user_id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  avatar_updated_at: string | null;
  notification_whatsapp_number: string | null;
  workspaces: Workspace[];
}

export function useActiveWorkspace() {
  const queryClient = useQueryClient();
  const workspaceId = useActiveWorkspaceStore((s) => s.workspace_id);
  const setWorkspaceId = useActiveWorkspaceStore((s) => s.setWorkspaceId);

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/api/me')
  });

  useEffect(() => {
    if (!me.data) return;
    const list = me.data.workspaces;
    if (list.length === 0) {
      if (workspaceId !== null) setWorkspaceId(null);
      return;
    }
    const known = workspaceId && list.some((w) => w.workspace_id === workspaceId);
    if (!known) {
      setWorkspaceId(list[0]!.workspace_id);
    }
  }, [me.data, workspaceId, setWorkspaceId]);

  const list = me.data?.workspaces ?? [];
  const active = list.find((w) => w.workspace_id === workspaceId) ?? null;
  const resolvingWorkspace = !!me.data && list.length > 0 && !active;
  const activeRole: WorkspaceRole | null = active?.role ?? null;
  const canConfigure = activeRole === 'owner' || activeRole === 'admin';

  return {
    workspace: active,
    list,
    me: me.data ?? null,
    loading: me.isLoading || resolvingWorkspace,
    activeRole,
    canConfigure,
    isOwner: activeRole === 'owner',
    isAdmin: activeRole === 'admin',
    isAgent: activeRole === 'agent',
    switchTo: (id: string) => {
      setWorkspaceId(id);
      queryClient.invalidateQueries();
    },
    refresh: () => queryClient.invalidateQueries({ queryKey: ['me'] })
  };
}
